import { API_ROOT } from "../services/apiClient";

/**
 * Phiên đăng nhập: access token ngắn hạn (authToken) + refresh token dài hạn (refreshToken).
 * installAuthRefresh() bọc window.fetch: request tới API có Bearer mà bị 401 → tự gọi
 * /auth/refresh (một lần cho mọi request đang chờ), lưu token mới rồi gửi lại request.
 */
const ACCESS_KEY = "authToken";
const LEGACY_ACCESS_KEY = "adminToken";
const REFRESH_KEY = "refreshToken";
export const TOKENS_REFRESHED_EVENT = "dalat-tokens-refreshed";

// Làm mới chủ động trước khi hết hạn để upload XHR (không qua fetch) vẫn có token hợp lệ
const PROACTIVE_MARGIN_MS = 2 * 60 * 1000;

const read = (k) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};

export function getRefreshToken() {
  return read(REFRESH_KEY);
}

export function storeTokens({ token, refreshToken }) {
  try {
    if (token) {
      localStorage.setItem(ACCESS_KEY, token);
      if (read(LEGACY_ACCESS_KEY)) localStorage.setItem(LEGACY_ACCESS_KEY, token);
    }
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  } catch {
    /* ignore */
  }
  scheduleProactiveRefresh();
  window.dispatchEvent(new Event(TOKENS_REFRESHED_EVENT));
}

export function clearRefreshToken() {
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
  clearTimeout(proactiveTimer);
}

/** Đăng xuất phía server (thu hồi refresh token); không chặn UI. */
export function revokeSession() {
  const refreshToken = getRefreshToken();
  clearRefreshToken();
  if (!refreshToken || !nativeFetch) return;
  nativeFetch(`${API_ROOT}/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
    keepalive: true,
  }).catch(() => {});
}

let nativeFetch = null;
let inflight = null;
let proactiveTimer = null;

async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  const res = await nativeFetch(`${API_ROOT}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (res.ok) {
    const data = await res.json();
    storeTokens(data);
    return data.token;
  }
  // Tab khác vừa làm mới trước (dùng chung localStorage) → dùng luôn token của tab đó
  if (getRefreshToken() !== refreshToken) return read(ACCESS_KEY);
  if (res.status === 401) clearRefreshToken();
  return null;
}

/** Làm mới token (gộp các lời gọi đồng thời). Trả access token mới hoặc null. */
export function refreshAccessToken() {
  if (!inflight) {
    inflight = doRefresh()
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

function tokenExpMs(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function scheduleProactiveRefresh() {
  clearTimeout(proactiveTimer);
  const token = read(ACCESS_KEY);
  if (!token || !getRefreshToken()) return;
  const exp = tokenExpMs(token);
  if (!exp) return;
  const delay = Math.max(5000, exp - Date.now() - PROACTIVE_MARGIN_MS);
  proactiveTimer = setTimeout(() => refreshAccessToken(), delay);
}

const isApiUrl = (url) => typeof url === "string" && url.startsWith(API_ROOT);
const isAuthEndpoint = (url) => /\/auth\/(login|google|refresh|logout|register|forgot-password)/.test(url);

function bearerOf(headers) {
  if (!headers) return null;
  const v = headers instanceof Headers ? headers.get("Authorization") : headers.Authorization || headers.authorization;
  return v && v.startsWith("Bearer ") ? v : null;
}

function withBearer(headers, token) {
  if (headers instanceof Headers) {
    const h = new Headers(headers);
    h.set("Authorization", `Bearer ${token}`);
    return h;
  }
  const h = { ...headers };
  delete h.authorization;
  h.Authorization = `Bearer ${token}`;
  return h;
}

export function installAuthRefresh() {
  if (typeof window === "undefined" || nativeFetch) return;
  nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const res = await nativeFetch(input, init);
    const url = typeof input === "string" ? input : null;
    if (res.status !== 401 || !isApiUrl(url) || isAuthEndpoint(url) || !bearerOf(init?.headers)) return res;
    if (!getRefreshToken()) return res;

    const newToken = await refreshAccessToken();
    if (!newToken) return res;
    return nativeFetch(input, { ...init, headers: withBearer(init.headers, newToken) });
  };

  scheduleProactiveRefresh();
}
